import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Cookies from "js-cookie";

const CATEGORIES = [
  "Agriculture",
  "Apparel",
  "Automobiles",
  "Beverages",
  "Construction",
  "Cosmetics",
  "Currencies",
  "Education",
  "Electronics",
  "Food",
  "Government",
  "Media",
  "Organizations",
  "Petroleum",
  "Pharmaceuticals",
];

const FieldLabel = ({ children, required }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 8,
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
  </div>
);

const SectionDivider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 16px" }}>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
    <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase" }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
  </div>
);

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const EditGenuineProduct = ({
  domain,
  show,
  handleClose,
  handleFormSubmit,
  productId,
}) => {
  const [userId, setUserId] = useState(null);

  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [price, setPrice] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");

  const [existingImages, setExistingImages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setUserId(Cookies.get("userId"));
  }, []);

  const canLoad = useMemo(
    () => !!(show && productId && userId),
    [show, productId, userId]
  );

  useEffect(() => {
    if (!canLoad) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await fetch(
          `/api/genuineProducts/${encodeURIComponent(productId)}?user=${encodeURIComponent(userId)}`,
          { method: "GET", redirect: "follow" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        if (cancelled) return;

        setBrand(data?.brand || "");
        setCategory(data?.category || "");
        setDescription(data?.description || "");
        setAddress(data?.address || "");
        setPrice(data?.price != null ? String(data.price) : "");

        const c = data?.contact || {};
        setPhone(c?.phone || "");
        setWhatsapp(c?.whatsapp || "");
        setEmail(c?.email || "");
        setWebsite(c?.website || "");

        const imgs =
          (Array.isArray(data?.imageUrls) && data.imageUrls) ||
          (Array.isArray(data?.images) && data.images) ||
          [];
        setExistingImages(imgs);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setErr("Failed to load product.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canLoad, productId, userId]);

  const safeUrl = (u) => {
    if (!u) return "";
    return /^https?:\/\//i.test(u) ? u : u;
  };

  const resetAndClose = () => {
    setErr("");
    setSaving(false);
    handleClose();
  };

  const saveChanges = async () => {
    if (!userId) { setErr("User not logged in."); return; }
    if (!productId) { setErr("Missing product id."); return; }
    if (!brand.trim()) { setErr("Product Name is required."); return; }
    if (!category) { setErr("Category is required."); return; }

    setSaving(true);
    setErr("");

    try {
      const contact =
        phone || whatsapp || email || website
          ? {
              ...(phone ? { phone } : {}),
              ...(whatsapp ? { whatsapp } : {}),
              ...(email ? { email } : {}),
              ...(website ? { website } : {}),
            }
          : undefined;

      const payload = {
        brand,
        category,
        description,
        address,
        price,
        ...(contact ? { contact } : {}),
        user: userId,
      };

      const res = await fetch(
        `/api/genuineProducts/${encodeURIComponent(productId)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          redirect: "follow",
        }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.error("Save failed:", res.status, txt);
        throw new Error(`HTTP ${res.status}`);
      }

      handleFormSubmit?.();
      resetAndClose();
    } catch (e) {
      console.error(e);
      setErr("Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const disabled = saving || loading;

  return (
    <>
      <style>{`
        .edit-genuine-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .edit-genuine-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .edit-genuine-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .edit-genuine-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
        .genuine-edit-select {
          width: 100%;
          background: #fff;
          border: 1.5px solid #e5e7eb;
          padding: 10px 36px 10px 14px;
          font-size: 14px;
          color: #111827;
          outline: none;
          appearance: none;
          -webkit-appearance: none;
          cursor: pointer;
          transition: border-color 0.15s, box-shadow 0.15s;
          border-radius: 10px;
        }
        .genuine-edit-select:focus {
          border-color: #ff5100;
          box-shadow: 0 0 0 3px rgba(255,81,0,0.1);
        }
        .genuine-edit-select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="edit-genuine-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-shield-alt" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Edit Genuine Product</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>Update product details</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {!userId && (
            <div style={{
              background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10,
              padding: "10px 14px", fontSize: 13, color: "#92400e", marginBottom: 14,
            }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
              You are not logged in.
            </div>
          )}

          {err && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10,
              padding: "10px 14px", fontSize: 13, color: "#991b1b", marginBottom: 14,
            }}>
              <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} />
              {err}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 22, marginBottom: 10, display: "block" }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>Loading product…</div>
            </div>
          ) : (
            <>
              {/* Existing images (read-only) */}
              {existingImages.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <FieldLabel>Product Images</FieldLabel>
                  <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
                    {existingImages.map((img, index) => {
                      const src = safeUrl(img);
                      return (
                        <div
                          key={`${img}-${index}`}
                          title="Images cannot be edited here"
                          style={{
                            width: 68, height: 68, borderRadius: 10, flexShrink: 0,
                            border: "1.5px solid #e5e7eb", background: "#f3f4f6", overflow: "hidden",
                          }}
                        >
                          {/^https?:\/\//i.test(src) ? (
                            <img
                              src={src}
                              alt={`existing-${index}`}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              loading="lazy"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div style={{
                              width: "100%", height: "100%", display: "flex",
                              alignItems: "center", justifyContent: "center",
                              fontSize: 11, fontWeight: 800, color: "#9ca3af",
                            }}>
                              {String(src).slice(0, 6)}…
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Product Name */}
              <div>
                <FieldLabel required>Product Name</FieldLabel>
                <input
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="e.g. Hand Sanitizer 500ml"
                  disabled={disabled}
                  style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                  {...focusHandlers}
                />
              </div>

              {/* Category */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel required>Category</FieldLabel>
                <div style={{ position: "relative" }}>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={disabled}
                    className="genuine-edit-select"
                  >
                    <option value="" disabled>Select a category…</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <i
                    className="fas fa-chevron-down"
                    style={{
                      position: "absolute", right: 14, top: "50%",
                      transform: "translateY(-50%)", color: "#9ca3af",
                      fontSize: 12, pointerEvents: "none",
                    }}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel>Description</FieldLabel>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the product"
                  rows={3}
                  disabled={disabled}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 80, opacity: disabled ? 0.6 : 1 }}
                  {...focusHandlers}
                />
              </div>

              {/* Address */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel>Address</FieldLabel>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Where can this product be found?"
                  rows={2}
                  disabled={disabled}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 64, opacity: disabled ? 0.6 : 1 }}
                  {...focusHandlers}
                />
              </div>

              {/* Price */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel>Price</FieldLabel>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0"
                    disabled={disabled}
                    style={{ ...inputStyle, paddingRight: 56, opacity: disabled ? 0.6 : 1 }}
                    {...focusHandlers}
                  />
                  <span style={{
                    position: "absolute", right: 14, top: "50%",
                    transform: "translateY(-50%)", fontSize: 12,
                    fontWeight: 700, color: "#9ca3af", pointerEvents: "none",
                  }}>
                    UGX
                  </span>
                </div>
              </div>

              <SectionDivider label="Contact Info" />

              {/* Phone + WhatsApp row */}
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <FieldLabel>Phone</FieldLabel>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+256 700 000 000"
                    disabled={disabled}
                    style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                    {...focusHandlers}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <FieldLabel>WhatsApp</FieldLabel>
                  <input
                    type="text"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="+256 700 000 000"
                    disabled={disabled}
                    style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                    {...focusHandlers}
                  />
                </div>
              </div>

              {/* Email + Website row */}
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <div style={{ flex: 1 }}>
                  <FieldLabel>Email</FieldLabel>
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@brand.com"
                    disabled={disabled}
                    style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                    {...focusHandlers}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <FieldLabel>Website</FieldLabel>
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="www.brand.com"
                    disabled={disabled}
                    style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                    {...focusHandlers}
                  />
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={saveChanges}
            disabled={disabled || !userId || !productId}
            style={{
              width: "100%",
              padding: "11px 0",
              borderRadius: 10,
              border: "none",
              background: (disabled || !userId || !productId) ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: (disabled || !userId || !productId) ? "#9ca3af" : "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: (disabled || !userId || !productId) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              transition: "background 0.15s",
            }}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                Saving…
              </>
            ) : (
              <>
                Save Changes
                <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditGenuineProduct;
